import Analytics from '@/utils/analytics';
import EmailIcon from '@assets/icons/email.svg';
import i18n from '@/utils/i18n';
import PhoneIcon from '@assets/icons/phone.svg';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AUTH_ROUTES } from '@/navigators/routes';
import { AUTH_STYLES, SCREEN_PADDING } from '@/styles';
import { AuthPageContainer } from '@/components/auth-page-container';
import { AuthService } from '@domain/auth/blocs/services/auth-service';
import { AuthStackParams } from '@/navigators/types';
import { Button } from '@/components/button';
import { EmailInput } from '@/components/email-input';
import { LoginMethod, VerificationMethod } from '@/enums';
import {
  Props as PhoneInputProps,
  PhoneNumberInput,
} from '@/components/phone-number-input';
import { SEND_ANSWER_PLACEHOLDER } from '@/utils/auth';
import { StackNavigationProp } from '@react-navigation/stack';
import { StyleSheet, View } from 'react-native';
import { SupplierSignupText } from '@/components/supplier-signup-text';
import { TabView, Props as TabViewProps } from '@/components/tab-view';
import { TEXT_INPUT_HEIGHT } from '@/components/text-input';
import { Type } from '@/components/toast';
import { useAuthStackContext } from '@/contexts/auth-stack-context';
import { useComponentFocus } from '@/hooks/use-component-focus';
import { useSendChallengeAnswer } from '@/hooks/use-send-challenge-answer';
import { useTheme } from '@/contexts/theme-context';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  navigation: StackNavigationProp<AuthStackParams, AUTH_ROUTES.SignIn>;
}

export interface TabRoute {
  key: LoginMethod;
}

const buildTabRoutes = (loginMethods: LoginMethod[]) =>
  loginMethods.map((method) => ({ key: method }));

const TAB_BAR_HEIGHT = 48;
const HEIGHT_WITHOUT_TAB_BAR = SCREEN_PADDING * 2 + TEXT_INPUT_HEIGHT;
const HEIGHT_WITH_TAB_BAR = HEIGHT_WITHOUT_TAB_BAR + TAB_BAR_HEIGHT;

export const SignInScreen: React.FC<Props> = ({ navigation }) => {
  const {
    translationsEnabled,
    loginMethods,
    defaultLoginMethod,
    loginRequestEnabled,
    navigateToBecomeCustomer,
    navigateToLoginRecovery,
  } = useAuthStackContext();
  const sendChallengeAnswer = useSendChallengeAnswer();
  const theme = useTheme();
  const { showToast } = useToasts();

  const [signingIn, setSigningIn] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [tabIndex, setTabIndex] = useState(-1);

  useComponentFocus({
    onFocus: useCallback(() => setSigningIn(false), []),
  });

  useEffect(() => {
    const defaultLoginIndex = loginMethods.indexOf(defaultLoginMethod);
    setTabIndex(defaultLoginIndex === -1 ? 0 : defaultLoginIndex);
  }, [loginMethods, defaultLoginMethod]);

  const onContinue = useCallback(
    async (loginMethod: LoginMethod, textValue: string | null) => {
      if (!textValue) {
        return showToast({
          content:
            loginMethod === LoginMethod.Phone
              ? i18n.t('toast.invalid_phone_number')
              : // TODO: get correct copy
                i18n.t('toast.invalid_email'),
          type: Type.WARNING,
        });
      }

      if (loginMethod === LoginMethod.Phone) {
        Analytics.track('sign_in.phone_number_form.submit', {
          phoneNumber: textValue,
        });
      } else {
        Analytics.track('sign_in.email_form.submit', {
          emailAddress: textValue,
        });
      }
      setSigningIn(true);
      try {
        // Cognito email login is case sensitive. Emails are always stored in
        // lower case so we will always send them to cognito in lower case
        const user = await AuthService.signIn(textValue.toLowerCase());
        /*
         * Cognito does not send client metadata to the sign in auth handler. The
         * only way we know what method to send the code to is by immediately
         * "resending" the code. The auth handler expects this, and will not send
         * a code until it it receives the metadata included in this request.
         *
         * Ensure code is sent by calling this before navigating to verification
         * code screen.
         */
        await sendChallengeAnswer(
          user,
          SEND_ANSWER_PLACEHOLDER,
          loginMethod === LoginMethod.Email
            ? VerificationMethod.Email
            : VerificationMethod.SMS,
          translationsEnabled,
        );
        return navigation.navigate(AUTH_ROUTES.SignInCode, {
          loginMethod,
          user,
        });
      } catch (e) {
        if (e instanceof Error) {
          showToast({
            content: e.message?.includes('HTTP 429')
              ? i18n.t('toast.sign_in_rate_limited')
              : i18n.t('toast.invalid_user'),
            type: Type.NEGATIVE,
          });
        }
      }

      setSigningIn(false);
    },
    [navigation, sendChallengeAnswer, showToast, translationsEnabled],
  );
  const tabRoutes = useMemo(() => buildTabRoutes(loginMethods), [loginMethods]);

  const navigationState = useMemo(
    () => ({
      index: tabIndex,
      routes: tabRoutes,
    }),
    [tabIndex, tabRoutes],
  );

  const phoneInputProps = useMemo<PhoneInputProps>(() => {
    return {
      containerStyle: AUTH_STYLES.inputContainer,
      label: null,
      onChangeText: setPhoneNumber,
      onSubmitEditing: () => onContinue(LoginMethod.Phone, phoneNumber),
      placeholder: i18n.t('phone_number'),
      returnKeyType: 'next',
      testID: 'phone-number-input',
    };
  }, [onContinue, phoneNumber]);

  const renderScene: TabViewProps<TabRoute>['renderScene'] = useCallback(
    ({ route }) => {
      const inputFocusable = tabRoutes[tabIndex].key === route.key;
      return route.key === LoginMethod.Phone ? (
        <View style={styles.inputWrapper}>
          <PhoneNumberInput {...phoneInputProps} focusable={inputFocusable} />
        </View>
      ) : (
        <View style={styles.inputWrapper}>
          <EmailInput
            containerStyle={AUTH_STYLES.inputContainer}
            focusable={inputFocusable}
            label={null}
            onChangeText={setEmailAddress}
            onSubmitEditing={() => onContinue(LoginMethod.Email, emailAddress)}
            placeholder={i18n.t('email')}
            testID="email-input"
            value={emailAddress}
          />
        </View>
      );
    },
    [emailAddress, onContinue, phoneInputProps, tabIndex, tabRoutes],
  );

  const renderTabBarLabel: TabViewProps<TabRoute>['label'] = useCallback(
    ({ route }: { route: TabRoute }) => {
      const IconComponent =
        route.key === LoginMethod.Phone ? PhoneIcon : EmailIcon;
      return (
        <IconComponent
          height={24}
          stroke={theme.colors.primary}
          testID={`tab-bar-${route.key.toLowerCase()}`}
          width={24}
        />
      );
    },
    [theme],
  );

  const hasMultipleLoginMethods = loginMethods.length > 1;
  const shouldRenderComponent = tabIndex >= 0;

  return (
    <AuthPageContainer title={i18n.t('log_in')}>
      {shouldRenderComponent ? (
        <>
          <View
            style={hasMultipleLoginMethods ? styles.tabView : styles.tabView2}
          >
            <TabView<TabRoute>
              animationEnabled={false}
              keyboardDismissMode="none"
              label={hasMultipleLoginMethods ? renderTabBarLabel : undefined}
              navigationState={navigationState}
              onIndexChange={setTabIndex}
              renderScene={renderScene}
              tabBarTabStyle={
                hasMultipleLoginMethods ? styles.tabBar : styles.hideTabBar
              }
            />
          </View>
          <View style={styles.bottom}>
            <Button
              loading={signingIn}
              onPress={() => {
                if (tabRoutes[tabIndex]?.key === LoginMethod.Phone) {
                  onContinue(LoginMethod.Phone, phoneNumber);
                } else {
                  onContinue(LoginMethod.Email, emailAddress);
                }
              }}
              trackingData={{ name: 'sign_in.continue_button' }}
            >
              {i18n.t('continue')}
            </Button>
            <View style={styles.signupText}>
              <SupplierSignupText
                loginRequestEnabled={loginRequestEnabled}
                navigateToBecomeCustomer={navigateToBecomeCustomer}
                navigateToLoginRecovery={navigateToLoginRecovery}
              />
            </View>
          </View>
        </>
      ) : null}
    </AuthPageContainer>
  );
};

const styles = StyleSheet.create({
  bottom: {
    flex: 1,
  },
  hideTabBar: {
    display: 'none',
  },
  inputWrapper: {
    marginVertical: SCREEN_PADDING,
  },
  signupText: {
    marginTop: 24,
  },
  tabBar: {
    height: TAB_BAR_HEIGHT,
  },
  tabView: {
    height: HEIGHT_WITH_TAB_BAR,
  },
  tabView2: {
    height: HEIGHT_WITHOUT_TAB_BAR,
  },
});
